/**
 * Full app reset for the mobile-web build ("Forgot password" recovery).
 *
 * The keystore main key encrypts every stored key with the password, so a
 * forgotten password cannot be reset in place — the only sound recovery is to
 * wipe the keystore + accounts + seeds (all app IndexedDB) and re-derive keys
 * from the Secret Recovery Phrase through the normal onboarding flow, which
 * forces a NEW password before any key is stored.
 *
 * Controllers run in a Web Worker (`mainController.worker.ts`) that holds
 * in-memory keystore secrets / accounts. Clearing storage alone does nothing
 * until that worker is torn down, so a hard `window.location.reload()` after the
 * wipe is mandatory.
 *
 * Gated to the mobile-web build: it uses `indexedDB.deleteDatabase` /
 * `window.location`, which are meaningless on native RN.
 */

// level-js prefixes the raw DB names (see `web/utils/railgunCore.ts` +
// `railgunBundledArtifactStore.ts`) — these are the on-disk IndexedDB names.
const KNOWN_DB_NAMES = [
  'zkx-mobile-web-storage', // app storage (storage.web.ts IDB_NAME)
  'level-js-zkx-railgun-db',
  'level-js-zkx-railgun-db-sw',
  'level-js-zkx-railgun-artifacts',
  'level-js-zkx-railgun-artifacts-sw',
  'level-js-railgun-zkey-cache-v1',
  // WalletConnect SignClient session/pairing store (@walletconnect/keyvaluestorage).
  // Lives in its OWN IndexedDB, separate from app storage — omit it and a WC
  // session survives a reset on the fallback path (Safari / no indexedDB.databases),
  // leaving the re-imported wallet showing a phantom "Connected" bound to the OLD
  // account. Must be wiped so reset is a genuine clean slate for dApp connections.
  'WALLET_CONNECT_V2_INDEXED_DB'
]

// A `blocked` delete (the worker still holds an open handle) must never hang the
// UX — race the wipe against this and reload regardless.
const WIPE_TIMEOUT_MS = 1500

const deleteDatabase = (name: string): Promise<void> =>
  new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name)
      req.onsuccess = () => resolve()
      req.onerror = () => resolve()
      req.onblocked = () => resolve()
    } catch {
      resolve()
    }
  })

const deleteAllDatabases = async (): Promise<void> => {
  let names: string[] = []

  // Fast path: enumerate every DB on this origin (it only hosts ZKX DBs).
  if (typeof indexedDB.databases === 'function') {
    try {
      const dbs = await indexedDB.databases()
      names = dbs.map((db) => db.name).filter((n): n is string => !!n)
    } catch {
      names = []
    }
  }

  // Fallback (e.g. Safari, no `.databases()`) or empty enumeration: delete the
  // known names. Union so we never miss a DB the enumeration did surface.
  const toDelete = Array.from(new Set([...names, ...KNOWN_DB_NAMES]))
  await Promise.all(toDelete.map(deleteDatabase))
}

const reload = () => {
  try {
    window.location.hash = '#/'
  } catch {
    // ignore — reload still resets the route below
  }
  window.location.reload()
}

/**
 * Wipe all app storage + IndexedDB, then hard-reload into fresh onboarding.
 * Never rejects; always reloads.
 */
export const resetMobileWebApp = async (): Promise<void> => {
  try {
    sessionStorage.clear()
  } catch {
    // best-effort
  }
  try {
    localStorage.clear()
  } catch {
    // best-effort
  }

  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, WIPE_TIMEOUT_MS)
  })

  await Promise.race([deleteAllDatabases(), timeout])

  reload()
}
